import React, { Dispatch, SetStateAction, useEffect } from 'react';
import {
  Button,
  Checkbox,
  Message,
  Table,
  TagPicker,
  useToaster,
} from 'rsuite';
import * as studentsApi from '../../api/students';
import CheckIcon from '@rsuite/icons/Check';
import DeleteIcon from '@rsuite/icons/Trash';
import { FormModal } from '../../common/FormModal';
import { renderTableEmpty, renderTableLoading } from '../../common/TableStates';
import { useConfirm } from '../../common/useConfirm';

type Props = {
  studentId: string;
  setStudentId: Dispatch<SetStateAction<string>>;
  coursesData: { value: string; label: string }[];
};

const { Column, HeaderCell, Cell } = Table;

export default function StudentQueuesModal({
  studentId,
  setStudentId,
  coursesData,
}: Props) {
  let cellClicked = false;
  const toaster = useToaster();
  const [confirm, ConfirmDialog] = useConfirm();
  const [selectedRows, setSelectedRows] = React.useState<string[]>([]);
  const [alreadyJoinedQueues, setAlreadyJoinedQueues] = React.useState<any[]>(
    []
  );
  const [selectedCoursesIds, setSelectedCoursesIds] = React.useState<string[]>(
    []
  );
  const [sortOrder, setSortOrder] = React.useState<'asc' | 'desc'>('asc');
  const [loading, setLoading] = React.useState<boolean>(false);

  const getStudentQueueEntries = async () => {
    setLoading(true);
    try {
      const response = await studentsApi.getQueueEntries(
        studentId,
        'createdAt',
        sortOrder.toUpperCase()
      );
      setAlreadyJoinedQueues(response.queue);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (studentId) {
      getStudentQueueEntries().catch(console.error);
    }
  }, [studentId, sortOrder]);

  const selection = ({ courseId }: { courseId: string }) => {
    if (selectedRows.includes(courseId)) {
      setSelectedRows(selectedRows.filter(r => r !== courseId));
    } else {
      setSelectedRows([...selectedRows, courseId]);
    }
  };
  return (
    <>
      {ConfirmDialog}
      <FormModal
        open={studentId.length > 0}
        onClose={() => {
          setStudentId('');
          setSelectedCoursesIds([]);
        }}
        title="Черги курсів студента"
        footer={
          <>
            <Button
              onClick={async () => {
                if (!selectedCoursesIds.length) {
                  setStudentId('');
                  setSelectedCoursesIds([]);
                  setAlreadyJoinedQueues([]);
                  return;
                }
                try {
                  await studentsApi.addToCoursesQueues(
                    studentId,
                    selectedCoursesIds
                  );
                  toaster.push(
                    <Message type="success" showIcon closable>
                      Черги оновлено
                    </Message>,
                    {
                      placement: 'topEnd',
                      duration: 1500,
                    }
                  );
                  setStudentId('');
                  setSelectedCoursesIds([]);
                  setAlreadyJoinedQueues([]);
                } catch (e: any) {
                  const errorMessage =
                    e.response?.data?.message ||
                    'Не вдалося додати у чергу. Спробуйте пізніше';
                  toaster.push(
                    <Message type="error" showIcon closable>
                      {errorMessage}
                    </Message>,
                    {
                      placement: 'topEnd',
                      duration: 1500,
                    }
                  );
                }
              }}
              appearance="primary"
            >
              Додати
            </Button>
            <Button
              onClick={() => {
                setStudentId('');
                setSelectedCoursesIds([]);
              }}
              appearance="subtle"
            >
              Скасувати
            </Button>
          </>
        }
      >
        <div style={{ display: 'flex' }}>
          <div>
            <label>Додати до черги курсів</label>
            <br />
            <TagPicker
              data={coursesData.filter(
                cD =>
                  !alreadyJoinedQueues.find(aJQ => aJQ.courseId === cD.value)
              )}
              onChange={setSelectedCoursesIds}
            />
            <Button
              onClick={async () => {
                try {
                  await studentsApi.addToCoursesQueues(
                    studentId,
                    selectedCoursesIds
                  );
                  toaster.push(
                    <Message type="success" showIcon closable>
                      Черг{selectedCoursesIds.length > 1 ? 'и' : 'у'} оновлено
                    </Message>,
                    {
                      placement: 'topEnd',
                      duration: 1500,
                    }
                  );
                  getStudentQueueEntries().catch(console.error);
                } catch (err: any) {
                  console.error(err);
                  const errorMessage =
                    err.response?.data?.message ||
                    'Не вдалося оновити черги. Спробуйте пізніше';
                  toaster.push(
                    <Message type="error" showIcon closable>
                      {errorMessage}
                    </Message>,
                    {
                      placement: 'topEnd',
                      duration: 1500,
                    }
                  );
                }
              }}
            >
              <CheckIcon />
            </Button>
          </div>
          <div style={{ marginLeft: 'auto' }}>
            {selectedRows.length > 0 && (
              <Button
                onClick={async () => {
                  const result = await confirm({
                    message: `Ви впевнені, що хочете видалити студента з черг ${selectedRows.length} курсів?`,
                    destructive: true,
                    confirmText: 'Видалити',
                  });
                  if (!result) {
                    return;
                  }
                  try {
                    await studentsApi.removeFromCoursesQueues(
                      studentId,
                      selectedRows
                    );
                    toaster.push(
                      <Message type="success" showIcon closable>
                        Студента видалено з черг
                        {selectedRows.length === 1 ? 'и' : ''}
                      </Message>,
                      {
                        placement: 'topEnd',
                        duration: 1500,
                      }
                    );
                    setSelectedRows([]);
                    getStudentQueueEntries().catch(console.error);
                  } catch (err: any) {
                    console.error(err);
                    const errorMessage =
                      err.response?.data?.message ||
                      'Не вдалося оновити чергу. Спробуйте пізніше';
                    toaster.push(
                      <Message type="error" showIcon closable>
                        {errorMessage}
                      </Message>,
                      {
                        placement: 'topEnd',
                        duration: 1500,
                      }
                    );
                  }
                }}
              >
                Видалити з черг{selectedRows.length === 1 ? 'и' : ''}
              </Button>
            )}
          </div>
        </div>
        <Table
          loading={loading}
          sortColumn={'createdAt'}
          sortType={sortOrder}
          onSortColumn={() => {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
          }}
          renderLoading={renderTableLoading}
          renderEmpty={renderTableEmpty}
          data={alreadyJoinedQueues}
        >
          <Column width={64} resizable={true}>
            <HeaderCell>
              <Button
                appearance="link"
                onClick={() => {
                  if (selectedRows.length === alreadyJoinedQueues.length) {
                    setSelectedRows([]);
                  } else {
                    setSelectedRows(alreadyJoinedQueues.map(q => q.courseId));
                  }
                }}
              >
                <CheckIcon />
              </Button>
            </HeaderCell>
            <Cell style={{ display: 'flex', alignItems: 'center' }}>
              {rowData => (
                <Checkbox
                  checked={selectedRows.includes(rowData.courseId)}
                  onCheckboxClick={() => {
                    selection({ courseId: rowData.courseId });
                  }}
                />
              )}
            </Cell>
          </Column>
          <Column width={320} resizable={true} fullText={true}>
            <HeaderCell>Курс</HeaderCell>
            <Cell dataKey="course.name" />
          </Column>
          <Column width={200} sortable={true} resizable={true}>
            <HeaderCell>Очікує з</HeaderCell>
            <Cell dataKey="createdAt">
              {rowData => new Date(rowData.createdAt).toLocaleString()}
            </Cell>
          </Column>

          <Column width={80} resizable={true}>
            <HeaderCell>Дії</HeaderCell>
            <Cell>
              {rowData => (
                <>
                  <Button
                    appearance="subtle"
                    onClick={async () => {
                      cellClicked = true;
                      const result = await confirm({
                        message: `Ви впевнені, що хочете видалити студента з черги ${rowData.course.name}?`,
                        destructive: true,
                        confirmText: 'Видалити',
                      });
                      if (!result) {
                        return;
                      }
                      try {
                        await studentsApi.removeFromCoursesQueues(studentId, [
                          rowData.courseId,
                        ]);
                        toaster.push(
                          <Message type="success" showIcon closable>
                            Студента видалено з черги
                          </Message>,
                          {
                            placement: 'topEnd',
                            duration: 1500,
                          }
                        );
                        getStudentQueueEntries().catch(console.error);
                      } catch (err: any) {
                        console.error(err);
                        const errorMessage =
                          err.response?.data?.message ||
                          'Не вдалося оновити чергу. Спробуйте пізніше';
                        toaster.push(
                          <Message type="error" showIcon closable>
                            {errorMessage}
                          </Message>,
                          {
                            placement: 'topEnd',
                            duration: 1500,
                          }
                        );
                      }
                    }}
                  >
                    <DeleteIcon />
                  </Button>
                </>
              )}
            </Cell>
          </Column>
        </Table>
        {selectedRows.length ? <p>Виділено: {selectedRows.length}</p> : <></>}
      </FormModal>
    </>
  );
}
